/**
 * Identification d'une image disque PS2, lue localement.
 *
 * Le code du jeu (SLES-504.80) figure dans SYSTEM.CNF, a la racine ISO 9660.
 * On parcourt le repertoire racine plutot que de balayer le fichier entier :
 * une ISO PS2 pese plus d'un gigaoctet, et seuls quelques secteurs nous
 * interessent.
 */

const SECTOR_SIZE = 2048;
const PVD_SECTOR = 16; // Primary Volume Descriptor, par norme ISO 9660.

export interface DiscInfo {
	code: string;
	region: string;
	title: string;
}

/** SLES / SCES = Europe, SLUS / SCUS = Amerique, SLPS / SCPS / SLPM = Japon. */
function regionFromCode(code: string): string {
	const prefix = code.slice(0, 4).toUpperCase();
	if (prefix === 'SLES' || prefix === 'SCES' || prefix === 'SLED') return 'PAL';
	if (prefix === 'SLUS' || prefix === 'SCUS' || prefix === 'SLUD') return 'NTSC-U';
	if (prefix === 'SLPS' || prefix === 'SCPS' || prefix === 'SLPM') return 'NTSC-J';
	return '';
}

async function readSectors(file: Blob, sector: number, count: number): Promise<DataView> {
	const start = sector * SECTOR_SIZE;
	const slice = file.slice(start, start + count * SECTOR_SIZE);
	return new DataView(await slice.arrayBuffer());
}

function ascii(view: DataView, offset: number, length: number): string {
	let out = '';
	for (let i = 0; i < length; i++) {
		const byte = view.getUint8(offset + i);
		if (byte === 0) break;
		out += String.fromCharCode(byte);
	}
	return out;
}

/**
 * Retourne le contenu de SYSTEM.CNF, ou null si l'image n'est pas une ISO 9660
 * exploitable (CSO/CHD compresses, image tronquee, fichier absent).
 */
async function readSystemCnf(file: Blob): Promise<string | null> {
	if (file.size < (PVD_SECTOR + 1) * SECTOR_SIZE) return null;

	const pvd = await readSectors(file, PVD_SECTOR, 1);
	// Signature "CD001" a l'octet 1 du descripteur.
	if (ascii(pvd, 1, 5) !== 'CD001') return null;

	// L'entree du repertoire racine occupe 34 octets a partir de l'octet 156.
	// Les champs numeriques sont stockes en double endianness ; on lit le
	// petit-boutiste, a l'offset +0 pour le LBA et +8 pour la taille.
	const rootLba = pvd.getUint32(156 + 2, true);
	const rootSize = pvd.getUint32(156 + 10, true);
	if (!rootLba || !rootSize || rootSize > 1048576) return null;

	const sectorCount = Math.ceil(rootSize / SECTOR_SIZE);
	const root = await readSectors(file, rootLba, sectorCount);

	let offset = 0;
	while (offset < rootSize) {
		const recordLength = root.getUint8(offset);
		if (recordLength === 0) {
			// Fin des entrees pour ce secteur : on saute au suivant.
			offset = (Math.floor(offset / SECTOR_SIZE) + 1) * SECTOR_SIZE;
			if (offset >= rootSize) break;
			continue;
		}
		const nameLength = root.getUint8(offset + 32);
		const name = ascii(root, offset + 33, nameLength).toUpperCase();
		// Le nom porte un suffixe de version ";1".
		if (name.startsWith('SYSTEM.CNF')) {
			const lba = root.getUint32(offset + 2, true);
			const size = root.getUint32(offset + 10, true);
			if (!size || size > SECTOR_SIZE * 4) return null;
			const content = await readSectors(file, lba, Math.ceil(size / SECTOR_SIZE));
			return ascii(content, 0, Math.min(size, content.byteLength));
		}
		offset += recordLength;
	}
	return null;
}

/**
 * Identifie une image disque. Ne leve jamais : une image non reconnue donne
 * simplement des champs vides, et la vignette se rabat sur le nom de fichier.
 */
export async function identifyDisc(file: Blob, fallbackTitle: string): Promise<DiscInfo> {
	const empty: DiscInfo = { code: '', region: '', title: fallbackTitle };
	try {
		const cnf = await readSystemCnf(file);
		if (!cnf) return empty;

		// Ligne de la forme : BOOT2 = cdrom0:\SLES_504.80;1
		const match = cnf.match(/BOOT2?\s*=\s*cdrom0:\\?([A-Z]{4})_?([0-9]{3})\.([0-9]{2})/i);
		if (!match) return empty;

		const code = `${match[1].toUpperCase()}-${match[2]}.${match[3]}`;
		return { code, region: regionFromCode(code), title: fallbackTitle };
	} catch {
		return empty;
	}
}
